import { useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { PaymentFailed } from "./payment-failed";
import { PaymentPending } from "./payment-pending";
import { PaymentSuccessful } from "./payment-successful";
import { Money, PaymentType } from "../../api/types";
import { useAppDispatch } from "../../store";
import { setPaymentArgs } from "../../store/slices/modalsSlice";
import { usePayment } from "./payment-context";
import { sanitizeErrorMessage } from "../../api/helpers/error";

export function PosPending({
  checkoutId,
  totalAmount,
  setPolling,
  paymentType,
  hasError,
  defaultErrorStatus: errorStatus,
}: {
  totalAmount: Money;
  checkoutId: string | null;
  paymentType: PaymentType;
  setPolling?: (value: boolean) => void;
  hasError?: boolean;
  defaultErrorStatus?: string;
}) {
  const dispatch = useAppDispatch();
  const [success, setSuccess] = useState(false);
  // const [error, setError] = useState(hasError);
  // const [errorStatus, setErrorStatus] = useState(defaultErrorStatus);
  const { makePayment, fetchPaymentStatus } = usePayment();

  async function checkStatus(checkoutId: string) {
    try {
      setPolling?.(false);
      const response = await fetchPaymentStatus(checkoutId);

      if (
        response.status == 502 ||
        response.data.result.status_code === "pending"
      ) {
        // Статус 502 - это таймаут соединения;
        // возможен, когда соединение ожидало слишком долго
        // и сервер (или промежуточный прокси) закрыл его
        // давайте восстановим связь
        await checkStatus(checkoutId);
      } else if (
        response.data.result.status_code === "checkout_fail" ||
        response.data.result.status_code === "retry"
      ) {
        dispatch(
          setPaymentArgs({
            checkoutId,
            type: paymentType,
            hasError: true,
            errorStatus:
              response.data.result.pos_status_code ||
              response.data.result.message,
          })
        );
      } else if (response.data.result.status_code === "checkout_success") {
        setSuccess(true);
        setPolling?.(false);
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status == 502) {
        await checkStatus(checkoutId);
      }
    }
  }

  const isFirstTry = useRef(true);

  useEffect(() => {
    if (checkoutId && isFirstTry.current) {
      checkStatus(checkoutId);
      isFirstTry.current = false;
    }
  }, [checkoutId]);

  const payForType = (paymentType: PaymentType) => {
    if (paymentType === PaymentType.App) {
      dispatch(setPaymentArgs(null));
    } else {
      // Reset error states
      dispatch(
        setPaymentArgs({
          checkoutId: null,
          type: paymentType,
          errorStatus: "",
          hasError: false,
        })
      );

      // Try payment again
      makePayment({
        type: paymentType,
        onCheckoutCreated: (checkoutId) => {
          // When checkout created, start polling for status
          checkStatus(checkoutId);
        },
      }).catch((error) => {
        const errorStatus = sanitizeErrorMessage(error.data);
        dispatch(
          setPaymentArgs({
            checkoutId: null,
            type: paymentType,
            hasError: true,
            errorStatus,
          })
        );
      });
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[264px]">
      {success ? (
        <PaymentSuccessful total={totalAmount} onPrintReceipt={console.log} />
      ) : hasError ? (
        <PaymentFailed
          paymentType={paymentType}
          statusCode={errorStatus}
          onRetry={() => {
            payForType(paymentType);
          }}
          onTryAnother={(newType) => {
            payForType(newType);
          }}
        />
      ) : (
        <PaymentPending paymentType={paymentType} statusCode={errorStatus} />
      )}
    </div>
  );
}
